import { useRef, useState } from 'react';
import { addClient, verifyGstin } from '../services/api';
import { showErrorToast, showLoadingToast, updateLoadingToastSuccess, updateLoadingToastError } from '../utils/toastConfig';
import { FiX, FiUser, FiHash, FiMapPin, FiMap, FiBriefcase, FiCheck, FiAlertCircle } from 'react-icons/fi';
import './AddClient.css';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
  'Andaman and Nicobar Islands', 'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep',
];

const AddClient = ({ closeModal, refreshClients }) => {
  const [form, setForm] = useState({
    client_name: '',
    address: '',
    gst_number: '',
    state: '',
    sector: '',
    tax_type: 'IGST', // 'GST' (intra-state CGST+SGST) or 'IGST' (inter-state)
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // GST verification UX (auto-triggered when the GSTIN reaches 15 chars).
  // status: 'idle' | 'verifying' | 'verified' | 'failed'
  const [gstStatus, setGstStatus] = useState('idle');
  const [gstMessage, setGstMessage] = useState('');
  // Tracks the last GSTIN we already attempted so we don't re-fire on every
  // keystroke if the user keeps the same 15-char value (e.g. focus toggles).
  const lastAttemptedGstinRef = useRef('');

  // Auto-verify implementation. Accepts the GSTIN explicitly so the caller
  // can pass the freshly-typed value without waiting for state to flush.
  const runGstVerification = async (gstin) => {
    if (!gstin) return;
    if (lastAttemptedGstinRef.current === gstin) return; // already tried this exact value
    lastAttemptedGstinRef.current = gstin;

    setGstStatus('verifying');
    setGstMessage('');
    try {
      const { data } = await verifyGstin(gstin);
      const d = data?.details || {};

      // Only fill empty fields so we never overwrite something the user already typed.
      // State is the one exception — it must match the GSTIN's state code, so we sync it.
      setForm((prev) => {
        // Guard: if the user has since changed the GSTIN, ignore this stale response.
        if ((prev.gst_number || '').toUpperCase() !== gstin) return prev;
        const next = { ...prev, gst_number: data?.gstin || gstin };
        if (!next.client_name) {
          next.client_name = d.legalName || d.tradeName || next.client_name;
        }
        if (!next.address && d.principalAddress) {
          next.address = d.principalAddress;
        }
        if (d.stateName && INDIAN_STATES.includes(d.stateName)) {
          next.state = d.stateName;
        }
        return next;
      });
      setGstStatus('verified');
      setGstMessage(`Verified${d.status ? ` · ${d.status}` : ''}`);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message
        || (status === 404 ? 'GSTIN not found in GST database'
          : status === 503 ? 'GST verification service not configured'
          : 'Verification failed');
      setGstStatus('failed');
      setGstMessage(msg);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'gst_number') {
      // Normalize to uppercase, drop spaces so a pasted "27 AAPFU…" still works.
      const normalized = value.toUpperCase().replace(/\s+/g, '').slice(0, 15);
      setForm((prev) => ({ ...prev, gst_number: normalized }));

      if (normalized.length < 15) {
        // Editing back below 15 chars — reset status and re-arm the auto-verify guard.
        if (gstStatus !== 'idle') {
          setGstStatus('idle');
          setGstMessage('');
        }
        lastAttemptedGstinRef.current = '';
        return;
      }

      // Exactly 15 chars — auto-verify (skipped if it's the same value we already tried).
      runGstVerification(normalized);
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.client_name || !form.gst_number || !form.address || !form.state || !form.sector) {
      showErrorToast('Please fill in all required fields');
      return;
    }

    setLoading(true);
    const toastId = showLoadingToast('Adding client...');

    try {
      await addClient(form);
      updateLoadingToastSuccess(toastId, 'Client added successfully!');
      refreshClients();
      setTimeout(() => {
        closeModal();
      }, 1000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to add client';
      setError(errorMsg);
      updateLoadingToastError(toastId, errorMsg);
      setLoading(false);
    }
  };

  return (
    <div className="ac-overlay" onClick={closeModal}>
      <div className="ac-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ac-header">
          <div>
            <h3>Add New Client</h3>
            <p className="ac-header-sub">Fill in the details to register a new client</p>
          </div>
          <button className="ac-close-btn" onClick={closeModal}>
            <FiX size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="ac-form">
          {error && <div className="ac-error">{error}</div>}

          <div className="ac-form-grid">
            <div className="ac-field">
              <label><FiUser size={13} /> Client Name *</label>
              <input
                type="text"
                name="client_name"
                value={form.client_name}
                onChange={handleChange}
                placeholder="Enter client name"
                required
              />
            </div>
            <div className="ac-field">
              <label><FiHash size={13} /> GST Number *</label>
              <input
                type="text"
                name="gst_number"
                value={form.gst_number}
                onChange={handleChange}
                placeholder="e.g., 36AABCU9603R1ZM"
                maxLength={15}
                required
                style={{ textTransform: 'uppercase' }}
              />
              {gstStatus === 'verifying' && (
                <div className="ac-gst-status">Verifying…</div>
              )}
              {gstStatus === 'verified' && (
                <div className="ac-gst-status ac-gst-ok">
                  <FiCheck size={13} /> {gstMessage || 'Verified'}
                </div>
              )}
              {gstStatus === 'failed' && (
                <div className="ac-gst-status ac-gst-err">
                  <FiAlertCircle size={13} /> {gstMessage || 'Verification failed'}
                </div>
              )}
            </div>
          </div>

          <div className="ac-field">
            <label><FiMapPin size={13} /> Address *</label>
            <textarea
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Enter full address"
              rows={3}
              required
            />
          </div>

          <div className="ac-form-grid">
            <div className="ac-field">
              <label><FiMap size={13} /> State *</label>
              <select name="state" value={form.state} onChange={handleChange} required>
                <option value="">Select State</option>
                {INDIAN_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="ac-field">
              <label><FiBriefcase size={13} /> Sector *</label>
              <select name="sector" value={form.sector} onChange={handleChange} required>
                <option value="">Select Sector</option>
                <option value="Education">Education</option>
                <option value="Information Technology">Information Technology</option>
              </select>
            </div>
          </div>

          {/* Tax regime — drives whether invoices for this client are billed
              CGST+SGST (intra-state) or IGST (inter-state). */}
          <div className="ac-field">
            <label>Tax Type *</label>
            <div className="ac-taxtype-group">
              <label className={`ac-taxtype-opt ${form.tax_type === 'GST' ? 'is-active' : ''}`}>
                <input
                  type="radio"
                  name="tax_type"
                  value="GST"
                  checked={form.tax_type === 'GST'}
                  onChange={handleChange}
                />
                <span className="ac-taxtype-text">
                  <span className="ac-taxtype-title">GST</span>
                  <span className="ac-taxtype-hint">CGST 9% + SGST 9% (intra-state)</span>
                </span>
              </label>
              <label className={`ac-taxtype-opt ${form.tax_type === 'IGST' ? 'is-active' : ''}`}>
                <input
                  type="radio"
                  name="tax_type"
                  value="IGST"
                  checked={form.tax_type === 'IGST'}
                  onChange={handleChange}
                />
                <span className="ac-taxtype-text">
                  <span className="ac-taxtype-title">IGST</span>
                  <span className="ac-taxtype-hint">IGST 18% (inter-state)</span>
                </span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="ac-actions">
            <button type="button" className="ac-btn-cancel" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" className="ac-btn-submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="ac-spinner"></span>
                  Adding...
                </>
              ) : (
                'Add Client'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClient;
